import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Privacy = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 },
    },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      
      <div className="max-w-4xl mx-auto px-4 py-20">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-primary hover:text-primary/80 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </motion.button>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary to-accent mb-4">
            Privacy Policy
          </h1>
          <p className="text-muted-foreground">
            Last updated: January 2026
          </p>
        </motion.div>

        {/* Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          {/* Section 1 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">1. Overview</h2>
            <p className="text-muted-foreground leading-relaxed">
              At Oman Drone Carnival, we are committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, and safeguard your data.
            </p>
          </motion.section>

          {/* Section 2 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">2. Information We Collect</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              We collect personal information including name, email address, phone number, and payment details when you:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
              <li>Register for the event</li>
              <li>Purchase tickets</li>
              <li>Contact us through our website</li>
              <li>Subscribe to our newsletter or updates</li>
            </ul>
          </motion.section>

          {/* Section 3 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">3. How We Use Your Information</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Your information is used solely for the following purposes:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
              <li>Processing event registrations and ticket purchases</li>
              <li>Sending event-related updates and communications</li>
              <li>Improving our services and user experience</li>
              <li>Responding to your inquiries and support requests</li>
            </ul>
          </motion.section>

          {/* Section 4 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">4. We Do Not Sell Your Data</h2>
            <p className="text-muted-foreground leading-relaxed font-semibold mb-2">
              Your privacy is paramount to us.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell, trade, rent, or share your personal information with third parties for marketing purposes. Your data is used exclusively for event management and communication purposes related to Oman Drone Carnival.
            </p>
          </motion.section>

          {/* Section 5 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">5. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement industry-standard security measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. All payment information is processed through secure, encrypted payment gateways.
            </p>
          </motion.section>

          {/* Section 6 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your personal information only for as long as necessary to fulfill the purposes outlined in this policy or as required by law.
            </p>
          </motion.section>

          {/* Section 7 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">7. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              You have the right to:
            </p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground ml-4">
              <li>Access your personal information</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Opt-out of marketing communications</li>
            </ul>
          </motion.section>

          {/* Section 8 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">8. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              Our website uses cookies to improve user experience and analyze site traffic. These cookies do not collect personally identifiable information. You can disable cookies in your browser settings, though this may affect website functionality.
            </p>
          </motion.section>

          {/* Section 9 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">9. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              For any privacy-related questions or to exercise your rights regarding your personal data, please contact us at:
            </p>
            <p className="text-primary mt-2">info@skiesseven.com</p>
          </motion.section>

          {/* Section 10 */}
          <motion.section variants={itemVariants} className="glass-effect border border-primary/10 p-8 rounded-lg">
            <h2 className="text-2xl font-heading font-bold text-primary mb-4">10. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated revision date.
            </p>
          </motion.section>
        </motion.div>
      </div>

    </div>
  );
};

export default Privacy;
